// Member Statement (cross-chit) — for a single subscriber, list every chit
// they're a member of with per-month payment history + payouts received.
// Typeahead picker for the subscriber. Period = current FY or all-time.

import { useEffect, useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { current_language } from '../i18n/index.js';
import { fiscal_year_label, format_inr, short_month_label } from '../lib/format.js';
import { friendly_error } from '../lib/friendly-error.js';
import { orpc } from '../lib/orpc.js';
import { Icon } from '../components/Icon.js';
import { DateFilterBar } from '../components/DateFilterBar.js';

interface ChitBlock {
  chit_id: string;
  chit_name_ta: string;
  chit_name_en: string;
  slot_number: number;
  membership_status: string;
  expected_to_date_paise: number;
  paid_to_date_paise: number;
  outstanding_paise: number;
  payouts_received_paise: number;
  net_position_paise: number;
  months: Array<{ due_month: string; expected_paise: number; paid_paise: number; status: string; last_payment_at: string | null }>;
  payouts: Array<{ cycle_index: number; cycle_date: string; receive_amount_paise: number }>;
}
interface MemberStatement {
  subscriber: {
    id: string;
    name_ta: string;
    name_en: string;
    phone: string;
    email: string | null;
    status: string;
    town_ta: string | null;
    town_en: string | null;
  };
  period: { fy_label: string | null; start: string | null; end: string | null; all_time: boolean };
  chits: ChitBlock[];
  totals: { contributed_paise: number; received_paise: number; net_paise: number };
}
interface SubMatch {
  id: string;
  name_ta: string;
  name_en: string;
  phone: string;
  email: string | null;
}

export function ReportsMemberStatementPage(): ReactElement {
  const lang = current_language();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const [params, set_params] = useSearchParams();
  const [picked_id, set_picked_id] = useState<string | null>(params.get('subscriber_id'));
  const [q_text, set_q_text] = useState('');
  const [period_mode, set_period_mode] = useState<'fy' | 'all_time'>('fy');
  const [fy_label, set_fy_label] = useState(fiscal_year_label());

  // Sync the picked subscriber to the URL so the operator can bookmark + share.
  useEffect(() => {
    if (picked_id) {
      params.set('subscriber_id', picked_id);
      set_params(params, { replace: true });
    } else {
      params.delete('subscriber_id');
      set_params(params, { replace: true });
    }
    // We omit `params` from deps intentionally — useSearchParams returns a new
    // ref every render; setting it inside an effect that depends on it loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked_id]);

  const search_q = useQuery({
    queryKey: ['report-subscriber-search', q_text],
    queryFn: () => orpc.reports.subscriber_search({ q: q_text }),
    enabled: q_text.length >= 2,
  });
  const matches = (search_q.data?.subscribers ?? []) as SubMatch[];

  const statement_q = useQuery({
    queryKey: ['report-member-statement', picked_id, period_mode, fy_label],
    queryFn: () =>
      orpc.reports.member_statement({
        subscriber_id: picked_id ?? '',
        ...(period_mode === 'fy' ? { fy_label } : { all_time: true }),
      }) as Promise<MemberStatement>,
    enabled: !!picked_id,
  });
  const data = statement_q.data;

  const export_csv = (): void => {
    if (!data) return;
    const lines = ['chit_id,chit_name,slot,due_month,expected_rupees,paid_rupees,status,last_payment_at'];
    for (const c of data.chits) {
      for (const m of c.months) {
        lines.push(
          [
            c.chit_id,
            (c.chit_name_en || c.chit_name_ta).replace(/,/g, ' '),
            c.slot_number,
            m.due_month,
            (m.expected_paise / 100).toFixed(2),
            (m.paid_paise / 100).toFixed(2),
            m.status,
            m.last_payment_at ?? '',
          ].join(','),
        );
      }
    }
    const blob = new Blob(['﻿', lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `member-statement-${data.subscriber.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [preview, set_preview] = useState<string | null>(null);
  const pdf_m = useMutation({
    mutationFn: () =>
      orpc.reports.member_statement_pdf({
        subscriber_id: picked_id ?? '',
        ...(period_mode === 'fy' ? { fy_label } : { all_time: true }),
      }),
    onSuccess: (out) => {
      if (out.preview_html) set_preview(out.preview_html);
      else window.open(out.pdf_url, '_blank');
    },
  });

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link to="/reports" style={{ color: 'var(--ink-3)', textDecoration: 'none', fontSize: 12 }}>
              ← {t('அறிக்கைகள்', 'Reports')}
            </Link>
          </div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('உறுப்பினர் அறிக்கை', 'Member Statement')}</div>
          <div className="h-sub">{period_mode === 'all_time' ? t('அனைத்து காலம்', 'All-time') : `FY ${fy_label}`}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {period_mode === 'fy' && <DateFilterBar mode="fy" fy_label={fy_label} on_change={set_fy_label} />}
          <button type="button" className={'btn sm' + (period_mode === 'fy' ? ' primary' : '')} onClick={() => set_period_mode('fy')}>{t('நிதியாண்டு', 'FY')}</button>
          <button type="button" className={'btn sm' + (period_mode === 'all_time' ? ' primary' : '')} onClick={() => set_period_mode('all_time')}>{t('அனைத்து காலம்', 'All-time')}</button>
          <button type="button" className="btn" onClick={export_csv} disabled={!data}>
            <Icon.download /> CSV
          </button>
          <button type="button" className="btn primary" onClick={() => pdf_m.mutate()} disabled={!picked_id || pdf_m.isPending}>
            <Icon.download /> {pdf_m.isPending ? '…' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Subscriber typeahead */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ padding: 14 }}>
          <input
            type="search"
            placeholder={t('பெயர் / ஃபோன் தேடவும்…', 'Search by name or phone…')}
            value={q_text}
            onChange={(e) => set_q_text(e.target.value)}
            style={{ width: '100%', maxWidth: 480, padding: '8px 10px', fontSize: 14, border: '1px solid var(--line)', borderRadius: 4, fontFamily: 'inherit' }}
          />
          {q_text.length >= 2 && (
            <div style={{ marginTop: 8, display: 'grid', gap: 4, maxHeight: 260, overflowY: 'auto', maxWidth: 480 }}>
              {search_q.isLoading && <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>{t('தேடுகிறது…', 'Searching…')}</div>}
              {!search_q.isLoading && matches.length === 0 && <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>{t('போட்டியில்லை', 'No matches')}</div>}
              {matches.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { set_picked_id(m.id); set_q_text(''); }}
                  style={{ display: 'flex', gap: 8, padding: '6px 8px', textAlign: 'left', background: m.id === picked_id ? 'var(--primary-soft)' : 'white', border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer' }}
                >
                  <span className={lang === 'ta' ? 'tamil' : ''} style={{ flex: 1 }}>{(lang === 'ta' ? m.name_ta : m.name_en) || '—'}</span>
                  <span className="mono" style={{ color: 'var(--ink-3)', fontSize: 11 }}>{m.phone}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {!picked_id && (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
          {t('உறுப்பினரை தேர்ந்தெடுக்கவும்', 'Pick a subscriber to begin')}
        </div>
      )}

      {statement_q.isLoading && <div style={{ padding: 20, color: 'var(--ink-3)' }}>{t('ஏற்றுகிறது…', 'Loading…')}</div>}
      {statement_q.error && <div style={{ padding: 20, color: 'var(--bad)' }}>{friendly_error(statement_q.error)}</div>}

      {data && (
        <>
          {/* Member profile */}
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }} className={lang === 'ta' ? 'tamil' : ''}>
                {(lang === 'ta' ? data.subscriber.name_ta : data.subscriber.name_en) || '—'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>
                {data.subscriber.phone}
                {data.subscriber.email ? ` · ${data.subscriber.email}` : ''}
                {(data.subscriber.town_en || data.subscriber.town_ta) ? ` · ${(lang === 'ta' ? data.subscriber.town_ta : data.subscriber.town_en) ?? ''}` : ''}
              </div>
            </div>
          </div>

          {/* Totals */}
          <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="kpi"><div className="kpi-label">{t('கட்டியது', 'Contributed')}</div><div className="kpi-value">{format_inr(data.totals.contributed_paise)}</div></div>
            <div className="kpi"><div className="kpi-label">{t('பெற்றது', 'Received')}</div><div className="kpi-value">{format_inr(data.totals.received_paise)}</div></div>
            <div className={'kpi' + (data.totals.net_paise < 0 ? '' : ' primary')}>
              <div className="kpi-label">{t('நிகர நிலை', 'Net position')}</div><div className="kpi-value">{format_inr(data.totals.net_paise)}</div>
            </div>
          </div>

          {data.chits.length === 0 && (
            <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--ink-3)' }}>
              {t('சீட்டுகளில் சேர்ந்திருக்கவில்லை', 'Not enrolled in any chits')}
            </div>
          )}

          {data.chits.map((c) => (
            <div key={c.chit_id} className="card" style={{ marginBottom: 12 }}>
              <div className="card-head">
                <div>
                  <div className={'card-title ' + (lang === 'ta' ? 'tamil' : '')}>
                    <Link to={`/chits/${c.chit_id}`} style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                      {(lang === 'ta' ? c.chit_name_ta : c.chit_name_en) || '—'}
                    </Link>
                    <span style={{ color: 'var(--ink-3)', fontSize: 11, marginLeft: 6 }}>{t('இடம்', 'Slot')} #{c.slot_number}</span>
                  </div>
                  <div className="card-sub" style={{ marginTop: 2 }}>
                    {t('எதிர்பார்ப்பு', 'Expected')} {format_inr(c.expected_to_date_paise)} · {t('பெற்றது', 'Paid')} {format_inr(c.paid_to_date_paise)} · {t('நிலுவை', 'Outstanding')} <span style={{ color: c.outstanding_paise > 0 ? 'var(--bad)' : 'var(--ink-3)' }}>{format_inr(c.outstanding_paise)}</span> · {t('பெற்ற பணம்', 'Received')} {format_inr(c.payouts_received_paise)}
                  </div>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead style={{ background: 'var(--bg-elev)' }}>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('மாதம்', 'Month')}</th>
                      <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('எதிர்பார்ப்பு', 'Expected')}</th>
                      <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('பெற்றது', 'Paid')}</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('நிலை', 'Status')}</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('கடைசி கொடுப்பனவு', 'Last payment')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.months.length === 0 ? (
                      <tr><td colSpan={5} style={{ padding: 14, textAlign: 'center', color: 'var(--ink-3)' }}>{t('வரிசை இல்லை', 'No ledger rows in period')}</td></tr>
                    ) : c.months.map((m) => (
                      <tr key={m.due_month} style={{ borderTop: '1px solid var(--line)' }}>
                        <td style={{ padding: '6px 10px' }}>{short_month_label(m.due_month, lang)} {m.due_month.slice(0, 4)}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }} className="mono">{format_inr(m.expected_paise)}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }} className="mono">{format_inr(m.paid_paise)}</td>
                        <td style={{ padding: '6px 10px', color: m.status === 'paid' ? 'var(--good)' : m.status === 'overdue' ? 'var(--bad)' : 'var(--ink-3)' }}>{m.status}</td>
                        <td style={{ padding: '6px 10px', color: 'var(--ink-3)', fontSize: 11 }}>{m.last_payment_at ? new Date(m.last_payment_at).toLocaleDateString(lang === 'ta' ? 'ta-IN' : 'en-IN') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {c.payouts.length > 0 && (
                <div style={{ padding: '8px 14px', borderTop: '1px solid var(--line)', background: 'var(--info-soft)', fontSize: 12 }}>
                  <strong>{t('பெற்ற பணம்', 'Payouts received')}:</strong>{' '}
                  {c.payouts.map((p, i) => (
                    <span key={i} style={{ marginRight: 12 }}>
                      {t('சுழற்சி', 'cycle')} {p.cycle_index} ({p.cycle_date}) — <span className="mono">{format_inr(p.receive_amount_paise)}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {pdf_m.error && <div style={{ color: 'var(--bad)', padding: 10 }}>{friendly_error(pdf_m.error)}</div>}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,24,20,0.45)', zIndex: 10000, padding: 16, overflow: 'auto' }}>
          <div style={{ maxWidth: 960, margin: 'auto', background: 'white', borderRadius: 8 }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>PDF preview (DRY_RUN)</div>
              <button type="button" className="btn sm" onClick={() => set_preview(null)}>Close</button>
            </div>
            <iframe title="ms-preview" srcDoc={preview} style={{ width: '100%', height: '80vh', border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}
